// VideoSlideGenerator.js - WITH AI MODERATION MESSAGE DISPLAY
import React, { useEffect, useState } from 'react';
import { useSlideGenerator } from '../hooks/useSlideGenerator';
import { useVideoSlideController } from '../hooks/useVideoSlideController';
import { useAuth } from '../AuthContext';
import SlideCard from './SlideCard';
import GenerateLayout from './GenerateLayout';
import LoadingState from './LoadingState';
import ErrorDisplay from './ErrorDisplay';
import PromptPage from './PromptPage';
import UpgradeModal from './UpgradeModal';
import AllExports from './AllExports';
import SlideContentList from './SlideContentList';
import { loadWebFonts } from '../canvas/fontLoader';
import { usePlanTracker } from '../hooks/usePlanTracker';
import Logo from '../assets/logo-sv.png';
import './VideoSlideGenerator.css';

function VideoSlideGenerator() {
  const { user, userPlan } = useAuth();

  const {
    isProcessing,
    processedSlides,
    processingError,
    moderationBlock, // ✅ NEW: Get moderation block from hook
    slideTemplates,
    isGeneratingVideo,
    videoGenerationError,
    videoGenerationProgress,
    slideImages,
    isFetchingImages,
    getSlideImageUrl,
    handleReset,
    changeSlideTemplate,
    downloadSlide,
    copySlideText,
    transcribeAudio,
    generateFromPrompt,
    generateMP4Video,
    setProcessedSlides,
    planTracker,
    setSlideTemplates,
    setSlideImages,
    audioUrl,
    setAudioUrl,
    originalAudioFile, 
  } = useSlideGenerator();

  const {
    fontsLoaded,
    setFontsLoaded,
    hasStarted,
    uploadMode,
    audioFile,
    audioPreview,
    audioFileName,
    audioDuration,
    setAudioDuration,
    userPrompt,
    showMP4Modal,
    setShowMP4Modal,
    showPNGModal,
    setShowPNGModal,
    isExportingPPTX,
    showResults,
    presentationTitle,
    searchTerm,
    setSearchTerm,
    slideLayoutFilters,
    audioExpiresAt,
    canvasRefs,
    audioInputRef,
    audioPlayerRef,
    filteredTemplatesList,
    getFilteredTemplatesForSlide,
    getLayoutFilter,
    formatTime,
    getLoadingMessage,
    getCurrentTemplate,
    onAudioChange,
    handlePromptGenerate,
    handleAudioGenerateWithPrompt,
    handleAudioProcess,
    handleLoadPresentation,
    handleCompleteReset,
    handleTemplateSelect,
    handleTemplateNext,
    handleTemplatePrev,
    handleDownloadAllPPTX,
    handleMP4Export,
    handleLayoutFilterChange,
    handleImageUpload,
  } = useVideoSlideController({
    user,
    processedSlides,
    slideTemplates,
    slideImages,
    isProcessing,
    isFetchingImages,
    getSlideImageUrl,
    handleReset,
    changeSlideTemplate,
    transcribeAudio,
    generateFromPrompt,
    generateMP4Video,
    setProcessedSlides,
    setSlideTemplates,
    setSlideImages,
    audioUrl,
    setAudioUrl,
    originalAudioFile,
  });

  const [selectedSlides, setSelectedSlides] = useState(new Set());
  const [isExportingPNG, setIsExportingPNG] = useState(false);
  const [mp4Duration, setMp4Duration] = useState(3);
  const [mp4Transition, setMp4Transition] = useState('none');
  const [includeAudio, setIncludeAudio] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalConfig, setUpgradeModalConfig] = useState({
    limitType: 'projects',
    suggestedPlan: null
  });

  const { usage } = usePlanTracker(user?.uid);

  useEffect(() => {
    loadWebFonts().then(() => {
      setFontsLoaded(true);
    });
  }, [setFontsLoaded]);

  useEffect(() => {
    if (showPNGModal) {
      const allIndices = processedSlides.map((_, i) => i);
      setSelectedSlides(new Set(allIndices));
    }
  }, [showPNGModal, processedSlides]);


 // In VideoSlideGenerator.js - around line 145
useEffect(() => {
  // Wait for the loading state to actually render in the DOM
  if ((isProcessing || isFetchingImages) && processedSlides.length === 0 && !moderationBlock) {
    setTimeout(() => {
      const loadingElement = document.querySelector('.loading-state-inline');
      if (loadingElement) {
        loadingElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'  // Centers it in the viewport
        });
      }
    }, 200); // Longer delay to ensure DOM is fully rendered
  }
}, [isProcessing, isFetchingImages, processedSlides.length, moderationBlock]);

  // Check if processingError is a limit error and show modal
  useEffect(() => {
  if (processingError) {
    const errorLower = processingError.toLowerCase();
    
    const isLimitError = 
      errorLower.includes('limit') ||
      errorLower.includes('exceeded') ||
      errorLower.includes('upgrade') ||
      errorLower.includes('plan');

    if (isLimitError) {
      let limitType = 'projects';
      let suggestedPlan = null;

      // ✅ DISTINGUISH: Project count limits vs Duration limits
      
      // 1. DURATION LIMITS (file is too long)
      if (errorLower.includes('too long') || 
          errorLower.includes('maximum') ||
          errorLower.includes('exceeds')) {
        limitType = 'duration';
        
        // Suggest plan based on what duration is mentioned
        if (processingError.includes('20') || processingError.includes('40')) {
          suggestedPlan = 'creator';
        } else if (processingError.includes('60')) {
          suggestedPlan = 'professional';
        }
      }
      // 2. PROJECT COUNT LIMITS (monthly usage exceeded)
      else if (errorLower.includes('audio limit reached') || 
               errorLower.includes('audio limit exceeded') ||
               errorLower.includes('prompt limit reached') ||
               errorLower.includes('prompt limit exceeded')) {
        limitType = 'projects';  // ✅ This is project count, not duration
        
        // Extract the suggested plan from error message if present
        if (errorLower.includes('starter')) {
          suggestedPlan = 'starter';
        } else if (errorLower.includes('creator')) {
          suggestedPlan = 'creator';
        } else if (errorLower.includes('professional')) {
          suggestedPlan = 'professional';
        }
      }
      // 3. MP4 DOWNLOAD LIMITS
      else if (errorLower.includes('mp4') || errorLower.includes('video download')) {
        limitType = 'mp4';
      }

     

      setUpgradeModalConfig({ limitType, suggestedPlan });
      setShowUpgradeModal(true);
    }
  }
}, [processingError]);

  const handleToggleSlide = (index) => {
    const newSelected = new Set(selectedSlides);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSlides(newSelected);
  };

  const handleSelectAllSlides = () => {
    if (selectedSlides.size === processedSlides.length) {
      setSelectedSlides(new Set());
    } else {
      const allIndices = processedSlides.map((_, i) => i);
      setSelectedSlides(new Set(allIndices));
    }
  };

  const handleExportPNG = async () => {
    if (selectedSlides.size === 0) return;

    setIsExportingPNG(true);
    const selectedIndices = Array.from(selectedSlides).sort((a, b) => a - b);
    
    for (const slideIndex of selectedIndices) {
      await downloadSlide(processedSlides[slideIndex], slideIndex);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsExportingPNG(false);
    setShowPNGModal(false);
  };

  const handleExportMP4Local = async () => {
    const options = {
      transition: mp4Transition,
     includeAudio: includeAudio && !!originalAudioFile, // ✅ Check file, not ID
      duration: mp4Duration,
    };
    
    
    
    await handleMP4Export(options);
  };

  // Helper functions
  const getTruncatedTitle = () => {
    if (!presentationTitle) return 'New Presentation';
    
    const maxLength = 35;
    if (presentationTitle.length <= maxLength) {
      return presentationTitle;
    }
    
    return presentationTitle.substring(0, maxLength) + '...';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    
    if (user.displayName) {
      const names = user.displayName.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    
    if (user.email) {
      const emailName = user.email.split('@')[0];
      return emailName.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  const getUserName = () => {
    if (!user) return 'You';
    if (user.displayName) return user.displayName;
    if (user.email) return user.email.split('@')[0];
    return 'You';
  };

  if (!hasStarted) {
    return (
      <GenerateLayout onLoadPresentation={handleLoadPresentation} onNewChat={handleCompleteReset}>
        <PromptPage
          audioInputRef={audioInputRef}
          onAudioChange={onAudioChange}
          onPromptGenerate={handlePromptGenerate}
          onLoadPresentation={handleLoadPresentation}
          onAudioGenerateWithPrompt={handleAudioGenerateWithPrompt}
          isProcessing={isProcessing}
          processingError={processingError}
        />
      </GenerateLayout>
    );
  }

  const loadingInfo = getLoadingMessage();
  const showRightPanelLoading = loadingInfo !== null && processedSlides.length === 0;

  return (
    <GenerateLayout onLoadPresentation={handleLoadPresentation} onNewChat={handleCompleteReset}>
      <div className={`slide-generator-split ${showResults ? 'has-results' : ''}`}>
        {/* LEFT PANEL - Media Control Panel */}
        <div className="left-panel">
          <div className="panel-header">
            <h2 title={presentationTitle || 'New Presentation'}>
              {getTruncatedTitle()}
              <svg
                style={{ width: '16px', height: '16px', marginLeft: '6px', display: 'inline-block' }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </h2>
          </div>

          {uploadMode === 'audio' && (
            <div className="audio-section">
              <div className="prompt-display-card">
                <div className="user-message-header">
                  <div className="user-avatar-small">
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="User" />
                    ) : (
                      getUserInitials()
                    )}
                  </div>
                  <div className="user-message-content">
                    <span className="user-name">{getUserName()}</span>
                    
                    <div className="prompt-content">
                      {userPrompt && userPrompt.trim().length > 0 && (
                        <div className="prompt-text" style={{ marginBottom: '1rem' }}>
                          {userPrompt}
                        </div>
                      )}
                      
                      <div className="audio-file-info-compact">
                        <div className="audio-icon">
                          {audioFile?.type.startsWith('video/') ? (
                           <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                          <line x1="12" x2="12" y1="19" y2="22"/>
                        </svg>
                          ) : (
                            <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                          <line x1="12" x2="12" y1="19" y2="22"/>
                        </svg>
                          )}
                        </div>
                        <div className="audio-details">
                          <h3>{audioFileName}</h3>
                          <p>{audioDuration > 0 ? `Duration: ${formatTime(audioDuration)}` : 'Video to slides'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ✅ NEW: AI Moderation Block Display for Audio Mode */}
              {moderationBlock && (
                <div className="moderation-block-card">
                  <div className="moderation-block-header">
                    <span className="block-icon">🚫</span>
                    <h3>Content Cannot Be Processed</h3>
                  </div>
                  
                  <div className="moderation-block-content">
                    <p className="block-reason">
                      <strong>Reason:</strong> {moderationBlock.reason}
                    </p>
                    
                    {moderationBlock.suggestion && (
                      <div className="block-suggestions">
                        <p className="suggestions-title">💡 Educational Alternatives:</p>
                        <div className="suggestions-text">
                          {moderationBlock.suggestion}
                        </div>
                      </div>
                    )}
                    
                    {moderationBlock.type === 'audio' && (
                      <p className="audio-warning">
                        ⚠️ The audio content you uploaded contains material that violates our policy. 
                        Please upload different audio.
                      </p>
                    )}
                    
                    {moderationBlock.type === 'instructions' && (
                      <p className="audio-warning">
                        ⚠️ The instructions you provided contain inappropriate content. 
                        Please try different instructions or leave them empty.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {audioFile?.type.startsWith('video/') ? (
                <video
                  ref={audioPlayerRef}
                  src={audioPreview}
                  style={{ display: 'none' }}
                  onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
                />
              ) : (
                <audio
                  ref={audioPlayerRef}
                  src={audioPreview}
                  style={{ display: 'none' }}
                  onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
                />
              )}


 {/* ✅ NEW: Add this ErrorDisplay */}
    {processingError && processedSlides.length === 0 && !moderationBlock && (
      <ErrorDisplay error={processingError} />
    )}
              {(isProcessing || isFetchingImages) && processedSlides.length === 0 && !moderationBlock && (
                <div className="loading-state-inline">
                  <div className="loading-content-inline">
                    <img src={Logo} alt="Logo" className="loading-logo" />
                    <div className="loading-text">
                      <h3>{isProcessing ? 'Generating your slides' : 'Finding images'}</h3>
                      <p>This may take a few moments</p>
                    </div>
                  </div>
                </div>
              )}

              {processedSlides.length > 0 && (
                <SlideContentList 
                  processedSlides={processedSlides}
                  onCopyText={copySlideText}
                />
              )}
            </div>
          )}

          {uploadMode === 'prompt' && (
            <div className="prompt-section">
              <div className="prompt-display-card">
                <div className="user-message-header">
                  <div className="user-avatar-small">
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="User" />
                    ) : (
                      getUserInitials()
                    )}
                  </div>
                  <div className="user-message-content">
                    <span className="user-name">{getUserName()}</span>
                    
                    <div className="prompt-content">
                      <div className="prompt-text">
                        {userPrompt}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ✅ NEW: AI Moderation Block Display for Prompt Mode */}
              {moderationBlock && (
                <div className="moderation-block-card">
                  <div className="moderation-block-header">
                    <span className="block-icon">🚫</span>
                    <h3>Content Cannot Be Processed</h3>
                  </div>
                  
                  <div className="moderation-block-content">
                    <p className="block-reason">
                      <strong>Reason:</strong> {moderationBlock.reason}
                    </p>
                    
                    {moderationBlock.suggestion && (
                      <div className="block-suggestions">
                        <p className="suggestions-title">💡 Educational Alternatives:</p>
                        <div className="suggestions-text">
                          {moderationBlock.suggestion}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}


{processingError && processedSlides.length === 0 && !moderationBlock && (
      <ErrorDisplay error={processingError} />
    )}

              {(isProcessing || isFetchingImages) && processedSlides.length === 0 && !moderationBlock && (
                <div className="loading-state-inline">
                  <div className="loading-content-inline">
                    <img src={Logo} alt="Logo" className="loading-logo" />
                    <div className="loading-text">
                      <h3>{isProcessing ? 'Generating your slides...' : 'Finding images...'}</h3>
                      <p>This may take a few moments</p>
                    </div>
                  </div>
                </div>
              )}

              {processedSlides.length > 0 && (
                <SlideContentList 
                  processedSlides={processedSlides}
                  onCopyText={copySlideText}
                />
              )}
            </div>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel">
          <div className="panel-header">
            <h2>
              {processedSlides.length > 0 ? 'Export Options' : 'Results'}
            </h2>
            
            {processedSlides.length > 0 && (
              <div className="export-buttons-group">
                <button 
                  className="download-all-png-button"
                  onClick={() => setShowPNGModal(true)}
                  disabled={!fontsLoaded}
                  title="Download selected slides as PNG"
                >
                  Images
                </button>
                <button 
                  className="download-all-pptx-button"
                  onClick={handleDownloadAllPPTX}
                  disabled={!fontsLoaded || isExportingPPTX}
                  title="Download all slides as PowerPoint"
                >
                  {isExportingPPTX ? 'Exporting...' : 'Powerpoint'}
                </button>
                <button 
                  className="download-all-mp4-button"
                 onClick={() => {
    const limitCheck = planTracker.canDownloadMP4();
    if (!limitCheck.allowed) {
      setUpgradeModalConfig({ 
        limitType: 'mp4', 
        suggestedPlan: null
      });
      setShowUpgradeModal(true);
      return;
    }
    setShowMP4Modal(true);  
  }}
                  disabled={!fontsLoaded || planTracker.isLoading}
                  title={
                    planTracker.isLoading 
                      ? "Loading..." 
                      : `MP4 Export (${planTracker.canDownloadMP4().remaining} remaining)`
                  }
                >
                  Videos {!planTracker.isLoading && `(${planTracker.canDownloadMP4().remaining})`}
                </button>
              </div>
            )}
          </div>

          <div className="results-container">
            {showRightPanelLoading && (
              <LoadingState 
                message={loadingInfo.message}
                submessage={loadingInfo.submessage}
              />
            )}

            {!showRightPanelLoading && processedSlides.length > 0 && (
              <div className="processed-slides">
                <div className="slides-grid">
                  {processedSlides.map((slide, index) => {
                    const currentTemplate = getCurrentTemplate(index);
                    const hasImage = !!getSlideImageUrl(index);
                    
                    return (
                      <SlideCard
                        key={index}
                        slide={slide}
                        index={index}
                        uploadMode={uploadMode}
                        currentTemplate={currentTemplate}
                        filteredTemplatesList={getFilteredTemplatesForSlide(index)}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onTemplateSelect={handleTemplateSelect}
                        hasImage={hasImage}
                        canvasRef={el => {
                          canvasRefs.current[index] = el;
                        }}
                        fontsLoaded={fontsLoaded}
                        onTemplatePrev={handleTemplatePrev}
                        onTemplateNext={handleTemplateNext}
                        formatTime={formatTime}
                        imageUrl={getSlideImageUrl(index)}
                        layoutFilter={getLayoutFilter(index)}  
                        onLayoutFilterChange={handleLayoutFilterChange} 
                        onImageUpload={handleImageUpload}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {!showRightPanelLoading && !processedSlides.length && (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>No results yet</h3>
                <p>
                  {uploadMode === 'audio'
                    ? 'Click "Generate Slides" to process your audio'
                    : uploadMode === 'prompt'
                    ? 'Your slides will appear here'
                    : 'Upload content to get started'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* All Export Modals */}
        <AllExports
          showPNGModal={showPNGModal}
          setShowPNGModal={setShowPNGModal}
          processedSlides={processedSlides}
          selectedSlides={selectedSlides}
          handleSelectAllSlides={handleSelectAllSlides}
          handleToggleSlide={handleToggleSlide}
          handleExportPNG={handleExportPNG}
          isExportingPNG={isExportingPNG}
          showMP4Modal={showMP4Modal}
          setShowMP4Modal={setShowMP4Modal}
          isGeneratingVideo={isGeneratingVideo}
          planTracker={planTracker}
          mp4Transition={mp4Transition}
          setMp4Transition={setMp4Transition}
          mp4Duration={mp4Duration}
          setMp4Duration={setMp4Duration}
          audioUrl={audioUrl}
          uploadMode={uploadMode}
          includeAudio={includeAudio}
          setIncludeAudio={setIncludeAudio}
          audioExpiresAt={audioExpiresAt}
          videoGenerationProgress={videoGenerationProgress}
          videoGenerationError={videoGenerationError}
          handleExportMP4Local={handleExportMP4Local}
        />

        <UpgradeModal
  isOpen={showUpgradeModal}
  onClose={() => setShowUpgradeModal(false)}
  currentPlan={planTracker.userPlan || userPlan || 'free'}
  limitType={upgradeModalConfig.limitType}
  usage={usage}
  suggestedPlan={upgradeModalConfig.suggestedPlan}
/>
      </div>
    </GenerateLayout>
  );
}

export default VideoSlideGenerator;